import { useHistory } from "react-router-dom";
import { firebaseLogout } from "../../../services/firebase";
import { apiClient, clearApiCache } from "../../../services/api";
import {
  androidGetFirebaseToken,
  androidSignOut,
  isAndroidApp,
} from "../../../tools/android";
import {
  iosAuthSignOut,
  iosDeleteCookies,
  iosGetFirebaseToken,
  isIOSApp,
} from "../../../tools/ios";
import { useFirebaseUser } from "./useFirebaseUser";
import {
  nativePushProbeAttemptedStore,
  nativePushTokenStore,
  webPushFbTokenStore,
} from "../stores/pushTokensStore";

import { clearStoredContentFilters } from "../../app/utils/contentFilters";
import { clearPostLoginRedirectPath } from "../../app/utils/postLoginRedirect";
import { clearRoomInviteCodeData } from "../../auth/stores/roomInviteCodeStore";
import { OauthRoomsConfig } from "../../../features/oauth";
import { gaPushEvent } from "../../../tools/analytics/base.analytics";
import { resetSessionEventScope } from "../../../tools/analytics/eventIdentity";
import { resetMixpanelIdentity } from "../../../hooks/use-mixpanel";
import { clearShareAttribution } from "../../../tools/analytics/shareAttribution";
import { clearFirstTouch } from "../../../tools/analytics/firstTouch";
import { closeDesktopSettings } from "../../app/stores/desktopSettingsStore";
import { clearRoomWelcome } from "../../../components/room/RoomWelcomeHost";
import { clearProfessionalRecordRequest } from "../../../components/room/ProfessionalRecordHost";

export function useLogOutUser() {
  const history = useHistory();
  const { isAuthenticatedFirebase: isAuthenticated } = useFirebaseUser();

  return async function (options?: { skipApiCalls?: boolean }) {
    try {
      if (options?.skipApiCalls !== true) {
        if (!isAuthenticated) {
          throw new Error("User not authenticated already.");
        }

        // API - Get Firebase token to unregister (cache-first, fallback to bridge on native)
        let fbCloudMessagingToken: string | null = null;
        if (isIOSApp() || isAndroidApp()) {
          fbCloudMessagingToken = nativePushTokenStore.get();
          // Fallback to bridge if cache missing (e.g. localStorage eviction)
          if (fbCloudMessagingToken === null) {
            try {
              fbCloudMessagingToken = isIOSApp()
                ? await iosGetFirebaseToken()
                : androidGetFirebaseToken();
            } catch (error) {
              console.error(
                "Failed to get native token on logout fallback:",
                error
              );
            }
          }
        } else {
          fbCloudMessagingToken = webPushFbTokenStore.get();
        }

        if (fbCloudMessagingToken) {
          for (let attempt = 0; attempt < 2; attempt++) {
            try {
              await apiClient.delete(
                "/webapp/private/notification/registrations",
                {
                  json: { registrationId: fbCloudMessagingToken },
                }
              );
              break;
            } catch {
              // Retry once; on final attempt swallow — logout still proceeds.
            }
          }
        }

        await apiClient.post("/webapp/private/auth/signout");
      }
    } finally {
      // Sign out Firebase FIRST so isAuthenticatedFirebase is false
      // before clearing the cache (prevents query refetch storm)
      await firebaseLogout().catch(console.error);
      if (isAndroidApp()) void androidSignOut().catch(console.error);

      // Fire-and-forget (void functions)
      iosAuthSignOut();
      iosDeleteCookies();

      // Cache — cancel in-flight requests, then clear
      clearApiCache();

      // Clear localStorage entries
      clearPostLoginRedirectPath();
      clearStoredContentFilters();
      clearRoomInviteCodeData();
      clearShareAttribution();
      // A first touch recorded before this account signed in must not credit
      // the next account created on this device.
      clearFirstTouch();
      webPushFbTokenStore.set(null);
      nativePushTokenStore.set(null);
      nativePushProbeAttemptedStore.set(false);

      // Analytics: fire logged_out event before resetting Mixpanel
      gaPushEvent("logged_out");
      // The impression counting scope is per authenticated identity, and this
      // is an SPA navigation: the next account on this device must not be
      // silenced by what this one was already shown.
      resetSessionEventScope();
      try {
        // Resets the identity AND re-stamps the client emitter, which
        // `mixpanel.reset()` on its own would clear. Never call `reset`
        // directly here: the events would keep arriving with no `emitter` and
        // quietly leave the device panel.
        resetMixpanelIdentity();
      } catch {
        // Mixpanel not initialized (e.g. development) — skip
      }

      // OAuth cleanup
      for (const c of OauthRoomsConfig) c.manager.logout();

      // The desktop settings modal is a memory store: left open, it would sit
      // waiting for the next account to sign in on this tab.
      closeDesktopSettings();
      // So is the room welcome: a welcome still pending for this account must
      // not open for the next one.
      clearRoomWelcome();
      // And so is a professional record asked for by a join.
      clearProfessionalRecordRequest();

      // ALWAYS redirect to the welcome front door (immediately, don't wait for
      // signouts) so logged-out users can reconnect.
      history.push("/welcome");
    }
  };
}
